import i18n from '../../../i18n';
import { stripEmoji } from './emoji';
import {
  sanitizeIdCn,
  sanitizeIdHk,
  sanitizeIdMo,
  validateIdCn,
  validateIdHk,
  validateIdMo,
} from './id-card';
import {
  resolveDecimalPlaces,
  sanitizeDecimal,
  sanitizeNumber,
  validateDecimal,
  validateNumber,
} from './number';
import {
  defaultMobileMaxLength,
  sanitizeDigits,
  sanitizePhoneIntl,
  sanitizeTel,
  validateMobile,
  validatePhoneIntl,
  validateTel,
} from './phone';

/**
 * 校验文案兜底（与 zh-CN 一致）。
 * 语言包异步未就绪、或某 key 缺失时仍能给出可读中文，避免空白/undefined。
 */
const DIFI_INPUT_MSG_FALLBACK = {
  Required: '请输入',
  InvalidFormat: '格式不正确',
  Number: '请输入纯数字',
  Decimal: '请输入正确的小数',
  Amount: '请输入正确的金额',
  Fund: '请输入正确的资金金额',
  IdCn: '身份证号格式不正确',
  IdHk: '香港身份证格式不正确',
  IdMo: '澳门身份证格式不正确',
  Mobile: '手机号格式不正确',
  Tel: '电话号码格式不正确',
  PhoneIntl: '国际电话格式不正确',
  NumberEn: '请输入英文或数字',
  BankAccount: '请输入英文、数字或连字符',
  English: '请输入纯英文',
  Email: '邮箱格式不正确',
} as const;

export type DiFiInputMsgKey = keyof typeof DIFI_INPUT_MSG_FALLBACK;

/** 校验时取文案：跟随当前语言包；bootstrap 前或 key 缺失则回落中文 */
export function diFiInputMsg(key: DiFiInputMsgKey): string {
  // current 初始壳可能尚无 DiFiInput，语言包到位后由 applyPackToCurrent 写入
  const fromPack = i18n.DiFiInput?.[key];
  if (typeof fromPack === 'string' && fromPack) return fromPack;
  return DIFI_INPUT_MSG_FALLBACK[key];
}

export type DiFiInputKind =
  | 'text'
  /** 纯数字（与 number 相同） */
  | 'digit'
  /** 纯数字 */
  | 'number'
  /** 小数；小数位由 decimalPlaces 控制（未传则不限制） */
  | 'decimal'
  /** 金额；默认 2 位小数，可用 decimalPlaces 覆盖 */
  | 'amount'
  /** 资金；默认 2 位小数，可用 decimalPlaces 覆盖 */
  | 'fund'
  | 'id-cn'
  | 'id-hk'
  | 'id-mo'
  | 'mobile'
  | 'tel'
  | 'phone-intl'
  /** 邮箱 */
  | 'email'
  | 'mail'
  /** 纯英文（含空格，用于英文名字等） */
  | 'english'
  /** 银行账户：英文、数字与连字符 `-`（兼容全球账号格式） */
  | 'bank-account'
  /** 数字 + 英文字母 */
  | 'number-en'
  | 'custom';

export type DiFiValidateResult = {
  valid: boolean;
  message?: string;
  value: string;
};

export type DiFiInputRuleOptions = {
  inputKind?: DiFiInputKind;
  /** 输入过程过滤正则；与 inputKind 同时存在时优先 */
  pattern?: RegExp | string;
  /** 完整值校验正则；与 inputKind 同时存在时优先 */
  validatePattern?: RegExp | string;
  areaCode?: string;
  /** 空值是否视为通过（默认 true，交给表单 required） */
  allowEmpty?: boolean;
  /**
   * 小数位数：
   * - decimal：未传不限制
   * - amount / fund：未传默认 2
   * - number / digit：忽略（始终纯整数）
   */
  decimalPlaces?: number;
};

function toRegExp(pattern: RegExp | string): RegExp {
  if (pattern instanceof RegExp) return pattern;
  return new RegExp(pattern);
}

/** 按内置类型或自定义 pattern 过滤输入中的非法字符 */
export function filterDiFiInputValue(
  value: string,
  options: DiFiInputRuleOptions,
  prev = '',
): string {
  // 所有类型先去表情。text 没有字符白名单，必须在这里滤；其它类型再走各自规则。
  value = stripEmoji(value);

  if (options.pattern != null) {
    const re = toRegExp(options.pattern);
    return re.test(value) ? value : prev;
  }

  switch (options.inputKind) {
    case 'digit':
    case 'number':
      return sanitizeNumber(value);
    case 'decimal':
    case 'amount':
    case 'fund':
      return sanitizeDecimal(value, resolveDecimalPlaces(options.inputKind, options.decimalPlaces));
    case 'mobile':
      return sanitizeDigits(value);
    case 'id-cn':
      return sanitizeIdCn(value);
    case 'id-hk':
      return sanitizeIdHk(value);
    case 'id-mo':
      return sanitizeIdMo(value);
    case 'tel':
      return sanitizeTel(value);
    case 'phone-intl':
      return sanitizePhoneIntl(value);
    case 'number-en':
      return value.replace(/[^A-Za-z0-9]/g, '');
    case 'bank-account':
      return value.replace(/[^A-Za-z0-9-]/g, '');
    case 'english':
      return value.replace(/[^A-Za-z\s]/g, '');
    case 'email':
    case 'mail':
      return value.replace(/[^\w.@+-]/g, '');
    case 'custom':
    case 'text':
    default:
      return value;
  }
}

/** 完整值校验；可供 Form rules 复用 */
export function validateDiFiInput(
  value: string,
  options: DiFiInputRuleOptions = {},
): DiFiValidateResult {
  const allowEmpty = options.allowEmpty !== false;
  const trimmed = value.trim();

  if (!trimmed) {
    return {
      valid: allowEmpty,
      message: allowEmpty ? undefined : diFiInputMsg('Required'),
      value,
    };
  }

  if (options.validatePattern != null) {
    const re = toRegExp(options.validatePattern);
    const valid = re.test(trimmed);
    return {
      valid,
      message: valid ? undefined : diFiInputMsg('InvalidFormat'),
      value,
    };
  }

  switch (options.inputKind) {
    case 'digit':
    case 'number': {
      const valid = validateNumber(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('Number'), value };
    }
    case 'decimal': {
      const places = resolveDecimalPlaces(options.inputKind, options.decimalPlaces);
      const valid = validateDecimal(trimmed, places);
      return { valid, message: valid ? undefined : diFiInputMsg('Decimal'), value };
    }
    case 'amount': {
      const places = resolveDecimalPlaces(options.inputKind, options.decimalPlaces);
      const valid = validateDecimal(trimmed, places);
      return { valid, message: valid ? undefined : diFiInputMsg('Amount'), value };
    }
    case 'fund': {
      const places = resolveDecimalPlaces(options.inputKind, options.decimalPlaces);
      const valid = validateDecimal(trimmed, places);
      return { valid, message: valid ? undefined : diFiInputMsg('Fund'), value };
    }
    case 'id-cn': {
      const valid = validateIdCn(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('IdCn'), value };
    }
    case 'id-hk': {
      const valid = validateIdHk(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('IdHk'), value };
    }
    case 'id-mo': {
      const valid = validateIdMo(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('IdMo'), value };
    }
    case 'mobile': {
      const valid = validateMobile(trimmed, options.areaCode ?? '+86');
      return { valid, message: valid ? undefined : diFiInputMsg('Mobile'), value };
    }
    case 'tel': {
      const valid = validateTel(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('Tel'), value };
    }
    case 'phone-intl': {
      const valid = validatePhoneIntl(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('PhoneIntl'), value };
    }
    case 'number-en': {
      const valid = /^[A-Za-z0-9]+$/.test(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('NumberEn'), value };
    }
    case 'bank-account': {
      const valid = /^[A-Za-z0-9-]+$/.test(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('BankAccount'), value };
    }
    case 'english': {
      const valid = /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/.test(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('English'), value };
    }
    case 'email':
    case 'mail': {
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      return { valid, message: valid ? undefined : diFiInputMsg('Email'), value };
    }
    case 'custom':
      return { valid: true, value };
    case 'text':
    default:
      return { valid: true, value };
  }
}

/** 未显式传 maxLength 时，按类型给出默认上限 */
export function getKindDefaultMaxLength(
  inputKind?: DiFiInputKind,
  areaCode?: string,
): number | undefined {
  switch (inputKind) {
    case 'id-cn':
      return 18;
    case 'id-hk':
      return 11;
    case 'id-mo':
      return 10;
    case 'mobile':
      return defaultMobileMaxLength(areaCode ?? '+86');
    case 'tel':
      return 50;
    case 'phone-intl':
      return 20;
    case 'email':
    case 'mail':
      return 100;
    case 'english':
      return 100;
    case 'number-en':
    case 'bank-account':
      return 50;
    case 'number':
    case 'digit':
      return 20;
    case 'decimal':
    case 'amount':
    case 'fund':
      return 0;
    case 'text':
      return 100;
    default:
      return undefined;
  }
}

/** 软键盘建议 */
export function getSuggestedInputMode(
  inputKind?: DiFiInputKind,
): JSX_InputMode | undefined {
  switch (inputKind) {
    case 'digit':
    case 'number':
    case 'mobile':
    case 'id-mo':
      return 'numeric';
    case 'decimal':
    case 'amount':
    case 'fund':
      return 'decimal';
    case 'tel':
    case 'phone-intl':
      return 'tel';
    case 'email':
    case 'mail':
      return 'email';
    default:
      return undefined;
  }
}

/** 避免依赖 DOM lib 的 inputMode 联合类型 */
type JSX_InputMode =
  | 'none'
  | 'text'
  | 'tel'
  | 'url'
  | 'email'
  | 'numeric'
  | 'decimal'
  | 'search';

export function hasValidationRules(options: DiFiInputRuleOptions): boolean {
  if (options.validatePattern != null) return true;
  const kind = options.inputKind;
  return (
    kind != null &&
    kind !== 'text' &&
    kind !== 'custom'
  );
}
